import React, { useState, useEffect } from 'react';
import { AIStarIcon, CheckIcon, ChevronDownIcon } from '../components/Icons';
import { ContactSection } from '../components/Layout';

// IndustriEst-specific icons
const NodeIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="6" r="3" stroke="currentColor" strokeWidth="2"/>
    <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="2"/>
    <circle cx="18" cy="18" r="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 9V12M12 12L6 15M12 12L18 15" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const BenchmarkIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3V21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 14L11 10L15 14L21 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="7" cy="14" r="1.5" fill="currentColor"/>
    <circle cx="11" cy="10" r="1.5" fill="currentColor"/>
    <circle cx="15" cy="14" r="1.5" fill="currentColor"/>
    <circle cx="21" cy="8" r="1.5" fill="currentColor"/>
  </svg>
);

const ProposalIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C5.45 2 4.95 2.22 4.59 2.59C4.22 2.95 4 3.45 4 4V20C4 20.55 4.22 21.05 4.59 21.41C4.95 21.78 5.45 22 6 22H18C18.55 22 19.05 21.78 19.41 21.41C19.78 21.05 20 20.55 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 13H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 17L14 15L16 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AgentIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
    <circle cx="9" cy="10" r="1.5" fill="currentColor"/>
    <circle cx="15" cy="10" r="1.5" fill="currentColor"/>
    <path d="M8 15C8 15 9.5 17 12 17C14.5 17 16 15 16 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const ScheduleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 10H21" stroke="currentColor" strokeWidth="2"/>
    <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <rect x="6" y="13" width="4" height="2" fill="currentColor"/>
    <rect x="6" y="17" width="6" height="2" fill="currentColor"/>
    <rect x="14" y="13" width="4" height="2" fill="currentColor"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
    <path d="M21 21L16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M11 8V14M8 11H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

function IndustriEst() {
  const [scrollY, setScrollY] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close lightbox on escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setLightboxImage(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      icon: <NodeIcon />,
      title: 'Node-Based Visualization',
      description: 'Areas and assemblies display as nodes that scale with cost. See where the money is at a glance. Drill down into any branch of the estimate tree.'
    },
    {
      icon: <BenchmarkIcon />,
      title: 'Real Benchmark Data',
      description: 'Production rates pulled from completed jobs in your database. Not industry averages — actual receipts from real crews on real projects.'
    },
    {
      icon: <ProposalIcon />,
      title: 'Estimate to Proposal',
      description: 'Estimate data flows directly into proposal generation. Boilerplate, scope, exclusions, clarifications — ready to send.'
    },
    {
      icon: <AgentIcon />,
      title: 'AI Agent Integration',
      description: 'An AI agent that works like a junior estimator doing the leg work. Scaffolds estimates, researches pricing, flags scope gaps — trying to keep you happy.'
    },
    {
      icon: <ScheduleIcon />,
      title: 'P6 Schedule Export',
      description: 'Output a Primavera P6-ready schedule directly from your estimate data. Durations, sequences, and resource loading derived from your line items.'
    },
    {
      icon: <SearchIcon />,
      title: 'Live Research',
      description: 'The agent searches current material pricing, finds subs in the area, checks local water tables. Not just running scripts — actually thinking.'
    },
  ];

  const workflow = [
    {
      step: '01',
      title: 'Email + Takeoff In',
      description: 'Paste the scope email. Upload a simple takeoff. The agent reads the work, identifies cost drivers, and starts building the estimate structure.'
    },
    {
      step: '02',
      title: 'Agent Scaffolds',
      description: 'Areas, assemblies, line items — scaffolded based on the scope. Production rates applied from your historical job data. AI analysis flags anything unusual.'
    },
    {
      step: '03',
      title: 'Proposal Out',
      description: 'Review the estimate, make your calls, hit generate. Full proposal with exclusions, clarifications, and your boilerplate. A week of work in 20 minutes.'
    }
  ];

  const heroBenefits = [
    'Scaffold an estimate from email + takeoff',
    'Real production rates from completed jobs',
    'Output proposals or P6 schedules'
  ];

  return (
    <>
      <section className="hero">
        <div className="hero-background">
          <div className="hero-grid"></div>
          <div className="hero-background-star" style={{ transform: `translate(-50%, -50%) translateY(${scrollY * 0.3}px)` }}>
            <AIStarIcon />
          </div>
          <div className="hero-gradient"></div>
        </div>
        <div className="container">
          <div className="hero-inner">
            <div className="hero-copy">
              <p className="hero-eyebrow">Estimating Software + AI Agent</p>
              <h1 className="hero-title">
                IndustriEst
              </h1>
              <p className="hero-subtitle">
                Construction estimating software built by a 26-year industry veteran who learned to code. Then taught an AI agent to use it.
              </p>
              <div className="hero-buttons">
                <button
                  className="btn btn-primary"
                  onClick={() => scrollToSection('demo')}
                >
                  See Demo
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => scrollToSection('features')}
                >
                  Features
                </button>
              </div>
            </div>
            <div className="hero-side">
              <div className="hero-side-card">
                <h3>What changes on day one</h3>
                <ul className="hero-benefits">
                  {heroBenefits.map((benefit, index) => (
                    <li key={index} className="hero-benefit">
                      <span className="hero-benefit-icon"><CheckIcon /></span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="hero-side-footer">Heavy Industrial • Commercial • Infrastructure</div>
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={() => scrollToSection('demo')}>
          <ChevronDownIcon />
        </div>
      </section>

      {/* Demo Video Section */}
      <section id="demo" className="results">
        <div className="container">
          <div className="section-header">
            <h2>See the agent in action</h2>
            <p>Watch the AI agent scaffold an estimate in real-time (7x speed).</p>
          </div>
          <div className="demo-video-container">
            <video 
              controls 
              playsInline
              poster="/screenshots/canvas-full.png"
              className="demo-video"
            >
              <source src="/IndustriEst_fast.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Screenshots Gallery */}
      <section className="screenshots">
        <div className="container">
          <div className="section-header">
            <h2>Built for real estimating work</h2>
            <p>Node-based visualization, detailed line items, and comprehensive reporting. Click to enlarge.</p>
          </div>
          <div className="screenshots-grid">
            <div className="screenshot-item screenshot-large" onClick={() => setLightboxImage({src: '/screenshots/canvas-detail.png', caption: 'Canvas View — Areas and assemblies as nodes, scaled by cost'})}>
              <img src="/screenshots/canvas-detail.png" alt="IndustriEst Canvas View - Node visualization showing project areas scaled by cost" />
              <div className="screenshot-caption">Canvas View — Areas and assemblies as nodes, scaled by cost</div>
            </div>
            <div className="screenshot-item" onClick={() => setLightboxImage({src: '/screenshots/table-clean.png', caption: 'Table View — Full estimate hierarchy with cost breakdown'})}>
              <img src="/screenshots/table-clean.png" alt="IndustriEst Table View - Hierarchical estimate breakdown" />
              <div className="screenshot-caption">Table View — Full estimate hierarchy with cost breakdown</div>
            </div>
            <div className="screenshot-item" onClick={() => setLightboxImage({src: '/screenshots/reports.png', caption: 'Reports — Per-unit metrics, cost breakdown, markups applied'})}>
              <img src="/screenshots/reports.png" alt="IndustriEst Reports - Project summary with metrics" />
              <div className="screenshot-caption">Reports — Per-unit metrics, cost breakdown, markups applied</div>
            </div>
            <div className="screenshot-item" onClick={() => setLightboxImage({src: '/screenshots/table-view.png', caption: 'Line Items — Labor, material, equipment, subs per item'})}>
              <img src="/screenshots/table-view.png" alt="IndustriEst Line Items - Detailed assembly view" />
              <div className="screenshot-caption">Line Items — Labor, material, equipment, subs per item</div>
            </div>
            <div className="screenshot-item" onClick={() => setLightboxImage({src: '/screenshots/edit-modal.png', caption: 'Item Detail — Full cost entry with notes and library lookup'})}>
              <img src="/screenshots/edit-modal.png" alt="IndustriEst Edit Modal - Item detail entry" />
              <div className="screenshot-caption">Item Detail — Full cost entry with notes and library lookup</div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            <img src={lightboxImage.src} alt={lightboxImage.caption} />
            <div className="lightbox-caption">{lightboxImage.caption}</div>
          </div>
        </div>
      )}

      {/* Features */}
      <section id="features" className="services">
        <div className="container">
          <div className="section-header">
            <h2>Built for how estimating actually works</h2>
            <p>Every feature designed by someone who's actually bid the work.</p>
          </div>
          <div className="services-grid">
            {features.map((feature, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Gap */}
      <section className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Construction software has a gap.</h2>
              <p>Takeoff tools measure stuff. PM tools track stuff. But between "here's the quantities" and "here's the bid" — you're on your own.</p>
              <p>That's where estimators live. Spreadsheets, gut feel, and hoping your numbers are close.</p>
              <p>I spent 26 years in heavy industrial concrete, 12 of those estimating steel mill foundations. Then I learned to code and built the estimating software I always wanted.</p>
              <p>Then I taught an AI agent to use it.</p>
              <p>The agent scaffolds estimates from an email and a simple takeoff. It pulls production rates from jobs we actually ran. It searches current material pricing, finds subs in the area, checks water tables before pricing excavation.</p>
              <p><strong>A week of estimating work in 20 minutes.</strong></p>
              <p className="founder-sig">- Keaton Valentine, Founder</p>
            </div>
            <div className="about-visual">
              <div className="about-highlight">
                <h3>Not just software — an estimator</h3>
                <div className="about-differentiators">
                  <div className="differentiator">
                    <h4>Junior estimator on demand.</h4>
                    <p>The agent does the leg work — researching, organizing, double-checking, flagging issues. You make the calls. It's trying to keep you happy.</p>
                  </div>
                  <div className="differentiator">
                    <h4>Your data, your advantage.</h4>
                    <p>Benchmark rates from YOUR completed jobs. Not national averages. Not RSMeans. Actual production from actual crews you ran.</p>
                  </div>
                  <div className="differentiator">
                    <h4>Beyond scripts.</h4>
                    <p>The agent thinks. It searches the web for pricing. Checks local conditions. Reads specs and flags scope gaps. Not hard-coded — actually intelligent.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="process">
        <div className="container">
          <div className="section-header">
            <h2>From email to proposal</h2>
            <p>The workflow that turns a week of estimating into 20 minutes.</p>
          </div>
          <div className="process-steps">
            {workflow.map((step) => (
              <div key={step.step} className="process-step">
                <span className="process-number">{step.step}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-content">
            <h2>See it estimate your work</h2>
            <p>5 minutes on a call. Tell me your niche — industrial, commercial, civil, whatever. Watch the agent scaffold an estimate for YOUR type of work with real pricing data. That's the pitch.</p>
            <ul className="cta-points">
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>Live demo — not a slide deck</span></li>
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>We'll estimate something in your niche</span></li>
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>See how the agent thinks, not just what it outputs</span></li>
            </ul>
            <button className="btn btn-primary" onClick={() => scrollToSection('contact')}>
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      <ContactSection />
    </>
  );
}

export default IndustriEst;
